/**
 * ProjectsList - displays project summaries with progress
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ProjectSummary } from '@services/api';

interface ProjectsListProps {
  projects: ProjectSummary[];
  isLoading?: boolean;
}

const defaultColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

export function ProjectsList({ projects, isLoading }: ProjectsListProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">{t('dashboard.projects')}</h3>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded bg-neutral-200 dark:bg-neutral-700" />
                <div className="flex-1">
                  <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/2 mb-1" />
                  <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded w-1/4" />
                </div>
              </div>
              <div className="h-2 bg-neutral-200 dark:bg-neutral-700 rounded w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-neutral-900 dark:text-white">{t('dashboard.projects')}</h3>
        <button
          onClick={() => navigate('/projects')}
          className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          {t('dashboard.viewAll')}
        </button>
      </div>
      {projects.length === 0 ? (
        <div className="text-center py-8">
          <svg
            className="mx-auto h-12 w-12 text-neutral-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
            />
          </svg>
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">{t('dashboard.noProjectsYet')}</p>
          <button
            onClick={() => navigate('/projects/new')}
            className="mt-3 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            {t('dashboard.createFirstProject')}
          </button>
        </div>
      ) : (
        <ul className="space-y-4">
          {projects.map((project, idx) => {
            const color = project.color ?? defaultColors[idx % defaultColors.length];

            return (
              <li
                key={project.id}
                onClick={() => navigate(`/projects/${project.id}`)}
                className="cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700 p-3 -mx-3 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: color }}
                  >
                    {project.key.substring(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                      {project.name}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      {t('dashboard.tasksCompleted', {
                        completed: project.completedTaskCount,
                        total: project.taskCount,
                      })}
                    </p>
                  </div>
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    {Math.round(project.completionRate)}%
                  </span>
                </div>
                <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${project.completionRate}%`,
                      backgroundColor: color,
                    }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
